import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';
import {
  API_KEY_LIMIT_MAX,
  API_KEY_RATE_MAX,
  type ApiKeyLimitBudget,
  type ApiKeyLimitMode,
  type ApiKeyLimitRow,
  type ApiKeyLimitsState,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner, TBody, THead, TRow, Table } from '../../components/ui';

/**
 * API-key limits: budgets belong to the ORGANISATION (every key it has shares
 * them), live OLT work is counted per OLT and split by how the OLT is reached
 * (a CLI session, or SNMP at a fraction of the cost), and a handful of
 * compatibility methods keep their own caps.
 *
 * The numbers are the operator's to settle and are meant to rise as the
 * platform's paths get faster — which is why every cost budget shows what its
 * kind of call has actually taken on this server. Ships OBSERVING: counting,
 * refusing nothing, until a superadmin switches enforcement on.
 *
 * Read by anyone who manages keys (their own organisation's counts); changed
 * by a superadmin (`infra.manage`), inline — no dialog — with a reason the
 * server audits. The platform's own suggested numbers are a superadmin's
 * working aid and are shown to nobody else (operator, 2026-09-26).
 */

const MODE_LABEL: Record<ApiKeyLimitMode, string> = {
  off: 'Off',
  observe: 'Observing',
  enforce: 'Enforced',
};

const MODE_SENTENCE: Record<ApiKeyLimitMode, string> = {
  off: 'Nothing is counted.',
  observe: 'Every key call is counted and a call over a budget is recorded — none is refused.',
  enforce: 'A call over a budget is answered 429 with Retry-After until that budget’s window resets.',
};

const GROUPS: Array<{ kind: ApiKeyLimitRow['kind']; title: string; blurb: string }> = [
  {
    kind: 'general',
    title: 'Every API-key call',
    blurb: 'The organisation’s overall budget, and how fast its keys may call.',
  },
  {
    kind: 'cost',
    title: 'By what the call costs',
    blurb:
      'On top of the overall budget. Live OLT work is counted per OLT, and an operation the OLT answers over SNMP spends from the SNMP budget — a path that gets faster moves to a larger one.',
  },
  {
    kind: 'method',
    title: 'Compatibility methods with a cap of their own',
    blurb:
      'Methods of the compatibility API whose cost on this platform earns them a budget of their own. Every other method counts against the overall budget only.',
  },
];

const per = (window: ApiKeyLimitRow['window']) => (window === 'hour' ? '/ hour' : window === 'tenMinutes' ? '/ 10 min' : '/ min');

const ms = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)} s` : `${n} ms`);

function ModePill({ mode }: { mode: ApiKeyLimitMode }) {
  return (
    <Pill state tone={mode === 'enforce' ? 'success' : mode === 'observe' ? 'info' : 'neutral'} dot>
      {MODE_LABEL[mode]}
    </Pill>
  );
}

/** A number being edited, as text so an empty box is not 0, with "no limit" apart. */
type Entry = { value: string; none: boolean };
type Draft = { limits: Record<ApiKeyLimitBudget, Entry>; rate: { perSecond: string; burst: string; off: boolean } };

function draftFrom(state: ApiKeyLimitsState, pick: 'limit' | 'suggested'): Draft {
  const limits = Object.fromEntries(
    state.budgets.map((row) => {
      const n = pick === 'limit' ? row.limit : row.suggested;
      return [row.id, { value: n === null ? '' : String(n), none: n === null }];
    }),
  ) as Record<ApiKeyLimitBudget, Entry>;
  const rate = pick === 'limit' ? state.rate : { ...state.rate, ...state.rate.suggested };
  return {
    limits,
    rate: {
      perSecond: rate.perSecond === null ? '' : String(rate.perSecond),
      burst: rate.burst === null ? '' : String(rate.burst),
      off: rate.perSecond === null || rate.burst === null,
    },
  };
}

const whole = (text: string, max: number) => {
  const n = Number(text);
  return text.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= max;
};

/**
 * What goes to the server: the numbers that changed — and, when the mode being
 * saved is ENFORCE, every number shown, so an enforced limit is the operator's
 * own choice and never moves because a suggestion in the code did.
 */
function changes(state: ApiKeyLimitsState, draft: Draft, mode: ApiKeyLimitMode) {
  const pin = mode === 'enforce';
  const limits: Partial<Record<ApiKeyLimitBudget, number | null>> = {};
  for (const row of state.budgets) {
    const d = draft.limits[row.id];
    const next = d.none ? null : Number(d.value);
    if (next !== row.limit || (pin && row.source === 'default')) limits[row.id] = next;
  }
  const perSecond = draft.rate.off ? null : Number(draft.rate.perSecond);
  const burst = draft.rate.off ? null : Number(draft.rate.burst);
  const rateChanged =
    perSecond !== state.rate.perSecond || burst !== state.rate.burst || (pin && state.rate.source === 'default');
  return { limits, rate: rateChanged ? { perSecond, burst } : null };
}

export function ApiKeyLimitsCard() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canEdit = can('infra.manage');
  const [editing, setEditing] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [mode, setMode] = useState<ApiKeyLimitMode>('observe');
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);

  // The counts move while the page is open; half a minute is fresh enough to decide from.
  const limits = useQuery({
    queryKey: ['api-key-limits'],
    queryFn: () => api.get<ApiKeyLimitsState>('/api-keys/limits'),
    refetchInterval: 30_000,
  });
  const state = limits.data;

  const save = useMutation({
    mutationFn: (current: ApiKeyLimitsState) => {
      const { limits: changed, rate } = changes(current, draft!, mode);
      return api.patch<ApiKeyLimitsState>('/api-keys/limits', {
        ...(mode !== current.mode ? { mode } : {}),
        ...(Object.keys(changed).length > 0 ? { limits: changed } : {}),
        ...(rate ? { rate } : {}),
        reason,
      });
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['api-key-limits'], next);
      setEditing(false);
      setDraft(null);
      setReason('');
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    },
  });

  const startEditing = () => {
    if (!state) return;
    setDraft(draftFrom(state, 'limit'));
    setMode(state.mode);
    setReason('');
    save.reset();
    setEditing(true);
  };

  const invalid =
    draft !== null &&
    (Object.values(draft.limits).some((d) => !d.none && !whole(d.value, API_KEY_LIMIT_MAX)) ||
      (!draft.rate.off &&
        (!whole(draft.rate.perSecond, API_KEY_RATE_MAX) ||
          !whole(draft.rate.burst, API_KEY_RATE_MAX) ||
          Number(draft.rate.burst) < Number(draft.rate.perSecond))));
  const dirty =
    state !== undefined &&
    draft !== null &&
    !invalid &&
    (() => {
      const c = changes(state, draft, mode);
      return mode !== state.mode || Object.keys(c.limits).length > 0 || c.rate !== null;
    })();

  const limitCell = (row: ApiKeyLimitRow) => {
    if (editing && draft) {
      const d = draft.limits[row.id];
      return (
        <div className="flex flex-col items-end gap-1">
          <Input
            type="number"
            min={1}
            max={API_KEY_LIMIT_MAX}
            aria-label={`${row.label} limit`}
            className="w-24 text-right"
            value={d.value}
            disabled={d.none}
            onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, [row.id]: { ...d, value: e.target.value } } })}
          />
          <label className="flex items-center gap-1.5 text-xs text-muted">
            <input
              type="checkbox"
              checked={d.none}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  limits: {
                    ...draft.limits,
                    [row.id]: { none: e.target.checked, value: e.target.checked ? '' : String(row.suggested ?? row.limit ?? 1) },
                  },
                })
              }
            />
            No limit
          </label>
        </div>
      );
    }
    if (row.limit === null) return <span className="text-muted">{row.kind === 'cost' ? 'Overall budget only' : 'No limit'}</span>;
    return (
      <span className={row.source === 'operator' ? 'font-medium text-ink' : 'text-ink'}>
        {row.limit.toLocaleString()} {per(row.window)}
        {canEdit && row.source === 'default' && <span className="block text-xs text-faint">suggested</span>}
      </span>
    );
  };

  const rows = (kind: ApiKeyLimitRow['kind']) => (state?.budgets ?? []).filter((b) => b.kind === kind);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Gauge className="size-4 text-muted" />
          API key limits
          {state && <ModePill mode={state.mode} />}
        </span>
      }
      action={
        canEdit && state && !editing ? (
          <Button size="sm" onClick={startEditing}>
            Change limits
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4 p-5">
        <p className="max-w-3xl text-sm text-muted">
          Budgets belong to the organisation — every key it has shares them — and live OLT work is counted per
          OLT. What the OLT answers over SNMP costs it a fraction of a CLI session, so it has its own, larger
          budgets; as a path gets faster its budget can grow, and the Measured column shows what each kind of
          call takes on this server. A person signed in to the platform is never counted.
        </p>

        {limits.isLoading && <Spinner />}
        {limits.error && <ErrorNotice error={limits.error} />}

        {state && (
          <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              <span>{MODE_SENTENCE[state.mode]}</span>
              {state.mode !== 'off' && (
                <span className="text-faint">Counting since {formatRelative(state.countingSince)} — counts restart with the API.</span>
              )}
              {state.modeSource === 'operator' && state.updatedAt !== null && (
                <span className="text-faint">
                  Last changed by {state.updatedBy ?? 'an administrator'} {formatRelative(state.updatedAt)}.
                </span>
              )}
              {saved && <span className="font-medium text-success">Saved.</span>}
            </div>
            {!state.settingReadable && (
              <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                The stored limits could not be read, so the platform’s own numbers are in force and nothing is refused.
                Saving again replaces the unreadable setting.
              </p>
            )}

            {GROUPS.map((group) => (
              <section key={group.kind} className="space-y-2">
                <div>
                  <h3 className="text-sm font-medium text-ink">{group.title}</h3>
                  <p className="text-xs text-faint">{group.blurb}</p>
                </div>
                <Table>
                  <THead>
                    <th className="th">Budget</th>
                    {canEdit && <th className="th text-right">Suggested</th>}
                    <th className="th text-right">Limit</th>
                    <th className="th text-right" title="What calls of this kind have taken on this server since the API started (p50 · p95)">
                      Measured
                    </th>
                    <th className="th text-right" title="The fullest organisation or OLT in the current window, and the key spending most of it">
                      Now
                    </th>
                    <th className="th text-right" title="The fullest any budget got in one window since counting began">
                      Highest
                    </th>
                    <th className="th text-right">{state.mode === 'enforce' ? 'Refused' : 'Over'}</th>
                  </THead>
                  <TBody>
                    {rows(group.kind).map((row) => (
                      <TRow key={row.id}>
                        <td className="td min-w-[18rem] whitespace-normal align-top">
                          <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                            {row.label}
                            {row.scope === 'olt' && <Pill tone="primary">per OLT</Pill>}
                          </div>
                          <p className="mt-0.5 max-w-xl text-xs text-muted">{row.covers}</p>
                          {canEdit && showWhy && (
                            <div className="mt-1.5 max-w-xl space-y-1 text-xs">
                              <p className="text-faint">
                                <span className="font-medium text-muted">Measured: </span>
                                {row.measured}
                              </p>
                              <p className="text-faint">
                                <span className="font-medium text-muted">Why: </span>
                                {row.why}
                              </p>
                            </div>
                          )}
                        </td>
                        {canEdit && (
                          <td className="td whitespace-nowrap text-right align-top tabular-nums text-muted">
                            {row.suggested === null ? '—' : `${row.suggested.toLocaleString()} ${per(row.window)}`}
                          </td>
                        )}
                        <td className="td whitespace-nowrap text-right align-top tabular-nums">{limitCell(row)}</td>
                        <td className="td whitespace-nowrap text-right align-top text-xs tabular-nums text-muted">
                          {row.timing ? (
                            <>
                              {ms(row.timing.p50Ms)} · {ms(row.timing.p95Ms)}
                              <span className="block text-faint">{row.timing.samples} calls</span>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="td whitespace-nowrap text-right align-top tabular-nums">
                          {row.busiestNow}
                          {(row.busiestLabel || row.topKey) && (
                            <span className="block text-xs text-faint">
                              {[row.busiestLabel, row.topKey ? `${row.topKey.name} ${row.topKey.count}` : null]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                        </td>
                        <td className="td text-right align-top tabular-nums">{row.peak}</td>
                        <td className={`td text-right align-top tabular-nums ${row.over > 0 ? 'font-medium text-warn' : ''}`}>{row.over}</td>
                      </TRow>
                    ))}
                    {group.kind === 'general' && (
                      <TRow>
                        <td className="td min-w-[18rem] whitespace-normal align-top">
                          <div className="font-medium text-ink">Burst rule</div>
                          <p className="mt-0.5 max-w-xl text-xs text-muted">
                            How fast the organisation’s keys may call: a steady rate a second, and how many may arrive at once.
                          </p>
                        </td>
                        {canEdit && (
                          <td className="td whitespace-nowrap text-right align-top tabular-nums text-muted">
                            {state.rate.suggested.perSecond} / s · {state.rate.suggested.burst} at once
                          </td>
                        )}
                        <td className="td whitespace-nowrap text-right align-top tabular-nums">
                          {editing && draft ? (
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min={1}
                                  max={API_KEY_RATE_MAX}
                                  aria-label="Calls a second"
                                  className="w-16 text-right"
                                  value={draft.rate.perSecond}
                                  disabled={draft.rate.off}
                                  onChange={(e) => setDraft({ ...draft, rate: { ...draft.rate, perSecond: e.target.value } })}
                                />
                                <span className="text-xs text-muted">/ s</span>
                                <Input
                                  type="number"
                                  min={1}
                                  max={API_KEY_RATE_MAX}
                                  aria-label="At once"
                                  className="w-16 text-right"
                                  value={draft.rate.burst}
                                  disabled={draft.rate.off}
                                  onChange={(e) => setDraft({ ...draft, rate: { ...draft.rate, burst: e.target.value } })}
                                />
                                <span className="text-xs text-muted">at once</span>
                              </div>
                              <label className="flex items-center gap-1.5 text-xs text-muted">
                                <input
                                  type="checkbox"
                                  checked={draft.rate.off}
                                  onChange={(e) =>
                                    setDraft({
                                      ...draft,
                                      rate: e.target.checked
                                        ? { perSecond: '', burst: '', off: true }
                                        : {
                                            perSecond: String(state.rate.suggested.perSecond),
                                            burst: String(state.rate.suggested.burst),
                                            off: false,
                                          },
                                    })
                                  }
                                />
                                Off
                              </label>
                            </div>
                          ) : state.rate.perSecond === null || state.rate.burst === null ? (
                            <span className="text-muted">Off</span>
                          ) : (
                            <span className={state.rate.source === 'operator' ? 'font-medium text-ink' : 'text-ink'}>
                              {state.rate.perSecond} / s · {state.rate.burst} at once
                              {canEdit && state.rate.source === 'default' && <span className="block text-xs text-faint">suggested</span>}
                            </span>
                          )}
                        </td>
                        <td className="td text-right align-top text-muted">—</td>
                        <td className="td text-right align-top text-muted">—</td>
                        <td className="td text-right align-top text-muted">—</td>
                        <td className={`td text-right align-top tabular-nums ${state.rate.over > 0 ? 'font-medium text-warn' : ''}`}>
                          {state.rate.over}
                        </td>
                      </TRow>
                    )}
                  </TBody>
                </Table>
              </section>
            ))}

            {canEdit && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShowWhy((v) => !v)}>
                  {showWhy ? 'Hide what the numbers rest on' : 'Show what the numbers rest on'}
                </Button>
              </div>
            )}

            {editing && draft && (
              <div className="space-y-3 rounded-lg border border-line bg-surface-2 p-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Mode" hint={MODE_SENTENCE[mode]}>
                    <select className="input" value={mode} onChange={(e) => setMode(e.target.value as ApiKeyLimitMode)}>
                      <option value="observe">Observe — count, refuse nothing</option>
                      <option value="enforce">Enforce — refuse over a budget</option>
                      <option value="off">Off — count nothing</option>
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Reason" hint="Recorded in the action log with the numbers before and after.">
                      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. final numbers agreed" />
                    </Field>
                  </div>
                </div>
                {save.error ? <ErrorNotice error={save.error} /> : null}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    loading={save.isPending}
                    disabled={!dirty || reason.trim().length < 3}
                    onClick={() => save.mutate(state)}
                  >
                    Save limits
                  </Button>
                  <Button size="sm" onClick={() => setDraft(draftFrom(state, 'suggested'))}>
                    Use the suggestions
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(false);
                      setDraft(null);
                    }}
                  >
                    Cancel
                  </Button>
                  {invalid ? (
                    <span className="text-xs text-danger">
                      Each limit is a whole number from 1 to {API_KEY_LIMIT_MAX.toLocaleString()}, or no limit; the burst
                      rule is 1 to {API_KEY_RATE_MAX.toLocaleString()} a second, and at least that many at once.
                    </span>
                  ) : mode === 'enforce' && state.mode !== 'enforce' ? (
                    <span className="text-xs text-muted">Enforcing keeps every number shown as the limit in force.</span>
                  ) : null}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
